import { filter, map, sortBy } from 'lodash'
import { forwardRef, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type PromptSelectInput_query$key } from '@/__generated__/PromptSelectInput_query.graphql'
import {
  PromptSelectTarget,
  SingleSelectInput,
  type SelectOption,
  type SingleSelectInputProps,
  type SingleSelectInputRef
} from '@/components'

// PostGraphile exposes no CATEGORY_ASC orderBy (enum column), so the query orders by
// SEQUENCE_ASC and we group by category client-side. lodash sortBy is stable, so the
// SEQUENCE_ASC order is preserved within each category.
const CATEGORY_ORDER: Record<string, number> = { ABOUT_ME: 0, DATING: 1, HOBBIES_INTERESTS: 2 }

export type PromptSelectInputProps = Omit<SingleSelectInputProps, 'options' | 'ref'> & {
  queryRef: PromptSelectInput_query$key | null | undefined
  excludeIds?: string[]
  testID?: string
}

export const PromptSelectInput = forwardRef<SingleSelectInputRef, PromptSelectInputProps>(
  ({ accessibilityLabel = 'Prompt', queryRef, excludeIds = [], testID, value, ...props }, ref) => {
    const queryData = useFragment(
      graphql`
        fragment PromptSelectInput_query on Query {
          prompts(filter: { deletedAt: { isNull: true } }, orderBy: [SEQUENCE_ASC]) {
            nodes {
              category
              question
              rowId
            }
          }
        }
      `,
      queryRef
    )

    return (
      <SingleSelectInput
        accessibilityLabel={accessibilityLabel}
        options={useMemo<SelectOption[]>(
          () =>
            map(
              sortBy(
                filter(
                  queryData?.prompts?.nodes,
                  (prompt) => !!prompt && (prompt.rowId === value || !excludeIds.includes(prompt.rowId))
                ),
                (prompt) => CATEGORY_ORDER[prompt!.category] ?? 99
              ),
              (prompt) => ({
                label: prompt!.question,
                value: prompt!.rowId
              })
            ),
          [queryData?.prompts?.nodes, excludeIds, value]
        )}
        ref={ref}
        renderTarget={({ ref: targetRef, ...targetProps }) => (
          <PromptSelectTarget
            ref={targetRef}
            testID={testID}
            {...targetProps}
          />
        )}
        value={value}
        {...props}
      />
    )
  }
)

PromptSelectInput.displayName = 'PromptSelectInput'
